import type { AttachmentsContext } from "../prompt-input-context/AttachmentsContext";
import { LocalAttachmentsContext } from "../prompt-input-context/LocalAttachmentsContext";
import { useOptionalPromptInputController } from "../prompt-input-context/useOptionalPromptInputController";
import type { FileUIPart } from "ai";
import { nanoid } from "nanoid";
import type { ChangeEventHandler } from "react";
import type { FormEventHandler } from "react";
import { useCallback } from "react";
import { useEffect } from "react";
import { useMemo } from "react";
import { useRef } from "react";
import { useState } from "react";
import { InputGroup } from "@src/components/ui/input-group";
import { cn } from "@src/lib/utils";
import type { PromptInputError } from "./PromptInputError";
import type { PromptInputProps } from "./PromptInputProps";

export const PromptInput = ({
  className,
  accept,
  multiple,
  globalDrop,
  syncHiddenInput,
  maxFiles,
  maxFileSize,
  validateFile,
  onError,
  onSubmit,
  inputGroupClassName,
  inputGroupStyle,
  children,
  ...props
}: PromptInputProps) => {
  // Try to use a provider controller if present
  const controller = useOptionalPromptInputController();
  const usingProvider = !!controller;

  // Refs
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Direct ref on the rendered <form> below — used to scope the form-level
  // drop listener. Attached on the form JSX element rather than discovered
  // via DOM traversal so the listener attaches synchronously on mount.
  const formRef = useRef<HTMLFormElement | null>(null);

  // ----- Local attachments (only used when no provider)
  const [items, setItems] = useState<(FileUIPart & { id: string })[]>([]);
  const files = usingProvider ? controller.attachments.files : items;
  const attachmentCountRef = useRef(files.length);
  useEffect(() => {
    attachmentCountRef.current = files.length;
  }, [files.length]);

  const openFileDialogLocal = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const matchesAccept = useCallback(
    (f: File) => {
      if (!accept || accept.trim() === "") {
        return true;
      }
      if (accept.includes("image/*")) {
        return f.type.startsWith("image/");
      }
      // NOTE: keep simple; expand as needed
      return true;
    },
    [accept]
  );

  const add = useCallback(
    (fileList: File[] | FileList) => {
      const incoming = Array.from(fileList);
      const validationResults = incoming.map((file) => ({
        file,
        error: validateFile?.(file),
      }));
      const customErrors = validationResults
        .map(({ error }) => error)
        .filter((error): error is PromptInputError => !!error);
      const customValidated = validationResults
        .filter(({ error }) => !error)
        .map(({ file }) => file);
      if (customErrors.length > 0) {
        onError?.(customErrors[0]);
      }
      if (customValidated.length === 0) {
        return;
      }

      const accepted = customValidated.filter((f) => matchesAccept(f));
      if (incoming.length && accepted.length === 0) {
        onError?.({
          code: "accept",
          message: "No files match the accepted types.",
        });
        return;
      }
      const withinSize = (f: File) =>
        maxFileSize ? f.size <= maxFileSize : true;
      const sized = accepted.filter(withinSize);
      if (accepted.length > 0 && sized.length === 0) {
        onError?.({
          code: "max_file_size",
          message: "All files exceed the maximum size.",
        });
        return;
      }

      const capacity =
        typeof maxFiles === "number"
          ? Math.max(0, maxFiles - attachmentCountRef.current)
          : undefined;
      const capped =
        typeof capacity === "number" ? sized.slice(0, capacity) : sized;
      if (typeof capacity === "number" && sized.length > capacity) {
        onError?.({
          code: "max_files",
          message: "Too many files. Some were not added.",
        });
      }
      attachmentCountRef.current += capped.length;

      if (usingProvider) {
        controller.__addAttachments(capped);
        return;
      }

      setItems((prev) =>
        prev.concat(
          capped.map((file) => ({
            id: nanoid(),
            type: "file" as const,
            url: URL.createObjectURL(file),
            mediaType: file.type,
            filename: file.name,
          }))
        )
      );
    },
    [
      controller,
      matchesAccept,
      maxFiles,
      maxFileSize,
      onError,
      usingProvider,
      validateFile,
    ]
  );

  const remove = usingProvider
    ? (id: string) => controller.attachments.remove(id)
    : (id: string) =>
        setItems((prev) => {
          const found = prev.find((file) => file.id === id);
          if (found?.url) {
            URL.revokeObjectURL(found.url);
          }
          return prev.filter((file) => file.id !== id);
        });

  const clear = usingProvider
    ? () => controller.attachments.clear()
    : () =>
        setItems((prev) => {
          for (const file of prev) {
            if (file.url) {
              URL.revokeObjectURL(file.url);
            }
          }
          return [];
        });

  const openFileDialog = usingProvider
    ? () => controller.attachments.openFileDialog()
    : openFileDialogLocal;

  // Let provider know about our hidden file input so external menus can call openFileDialog()
  useEffect(() => {
    if (!usingProvider) {
      return;
    }
    controller.__registerFileInput(
      inputRef,
      () => inputRef.current?.click(),
      add
    );
  }, [usingProvider, controller, add]);

  // Note: File input cannot be programmatically set for security reasons
  // The syncHiddenInput prop is no longer functional
  useEffect(() => {
    if (syncHiddenInput && inputRef.current && files.length === 0) {
      inputRef.current.value = "";
    }
  }, [files, syncHiddenInput]);

  // Attach drop handlers on nearest form and document (opt-in).
  // Skipped when the provider owns a wider drop zone (`dropZoneRef`) —
  // otherwise dropping on the form (which is inside the drop zone) would
  // bubble to both listeners and add the files twice.
  const providerOwnsDropZone =
    usingProvider && controller.attachments.hasDropZone;
  useEffect(() => {
    if (providerOwnsDropZone) {
      return;
    }
    const form = formRef.current;
    if (!form) {
      return;
    }

    const onDragOver = (e: DragEvent) => {
      if (e.dataTransfer?.types?.includes("Files")) {
        e.preventDefault();
      }
    };
    const onDrop = (e: DragEvent) => {
      if (e.dataTransfer?.types?.includes("Files")) {
        e.preventDefault();
      }
      if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
        add(e.dataTransfer.files);
      }
    };
    form.addEventListener("dragover", onDragOver);
    form.addEventListener("drop", onDrop);
    return () => {
      form.removeEventListener("dragover", onDragOver);
      form.removeEventListener("drop", onDrop);
    };
  }, [add, providerOwnsDropZone]);

  useEffect(() => {
    if (!globalDrop) {
      return;
    }

    const onDragOver = (e: DragEvent) => {
      if (e.dataTransfer?.types?.includes("Files")) {
        e.preventDefault();
      }
    };
    const onDrop = (e: DragEvent) => {
      if (e.dataTransfer?.types?.includes("Files")) {
        e.preventDefault();
      }
      if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
        add(e.dataTransfer.files);
      }
    };
    document.addEventListener("dragover", onDragOver);
    document.addEventListener("drop", onDrop);
    return () => {
      document.removeEventListener("dragover", onDragOver);
      document.removeEventListener("drop", onDrop);
    };
  }, [add, globalDrop]);

  // Latest locally-owned attachments, read by the unmount cleanup below without
  // making that effect re-run when they change.
  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // Revoke object URLs for locally-owned attachments on unmount only. `items`
  // must NOT be a dependency: React runs an effect's cleanup before every
  // re-run, so listing it revoked the blob URLs of attachments that were still
  // mounted (adding a second file revoked the first file's URL). Their previews
  // then broke and `fetch(blob:...)` on submit rejected with "Failed to fetch".
  // `remove`/`clear` already revoke the URLs they drop, and when a provider owns
  // the attachments it owns their lifecycle too.
  useEffect(
    () => () => {
      if (usingProvider) {
        return;
      }
      for (const f of itemsRef.current) {
        if (f.url) {
          URL.revokeObjectURL(f.url);
        }
      }
    },
    [usingProvider]
  );

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const input = event.currentTarget;
    if (input.files) {
      add(input.files);
    }
    // Reset so picking the same file twice in a row still fires `onChange`.
    // Without this, re-selecting an identical filename looks like a no-op.
    input.value = "";
  };

  const convertBlobUrlToDataUrl = async (url: string): Promise<string> => {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const ctx = useMemo<AttachmentsContext>(
    () => ({
      files: files.map((item) => ({ ...item, id: item.id })),
      add,
      remove,
      clear,
      openFileDialog,
      fileInputRef: inputRef,
      // Drop-zone affordance is a provider-level feature; the form-only
      // context never owns a drop zone or surfaces drag state.
      isDraggingFiles: false,
      hasDropZone: false,
    }),
    [files, add, remove, clear, openFileDialog]
  );

  const handleSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();

    const form = event.currentTarget;
    const text = usingProvider
      ? controller.textInput.value
      : (() => {
          const formData = new FormData(form);
          return (formData.get("message") as string) || "";
        })();

    // Reset form immediately after capturing text to avoid race condition
    // where user input during async blob conversion would be lost
    if (!usingProvider) {
      form.reset();
    }

    // Convert blob URLs to data URLs asynchronously
    void Promise.all(
      files.map(async ({ id, ...item }) => {
        if (item.url && item.url.startsWith("blob:")) {
          return {
            ...item,
            url: await convertBlobUrlToDataUrl(item.url),
          };
        }
        return item;
      })
    )
      .then((convertedFiles: FileUIPart[]) => {
        try {
          const result = onSubmit({ text, files: convertedFiles }, event);

          // Handle both sync and async onSubmit
          if (result instanceof Promise) {
            result
              .then(() => {
                clear();
                if (usingProvider) {
                  controller.textInput.clear();
                }
              })
              .catch(() => {
                // Don't clear on error - user may want to retry
              });
          } else {
            // Sync function completed without throwing, clear attachments
            clear();
            if (usingProvider) {
              controller.textInput.clear();
            }
          }
        } catch (error) {
          // Don't clear on error - user may want to retry
        }
      })
      .catch((error: unknown) => {
        // Reading an attachment back out of its blob URL failed, so `onSubmit`
        // was never called. Without this the rejection went unhandled and the
        // submit vanished silently after the form had already been reset — the
        // user saw their message disappear with no error. Attachments are kept
        // so the send can be retried.
        onError?.({
          code: "attachment_read",
          message: "Could not read one or more attachments.",
          cause: error,
        });
        if (!usingProvider) {
          const messageInput = form.elements.namedItem("message");
          if (
            (messageInput instanceof HTMLInputElement ||
              messageInput instanceof HTMLTextAreaElement) &&
            messageInput.value === ""
          ) {
            messageInput.value = text;
          }
        }
      });
  };

  // Render with or without local provider
  const inner = (
    <>
      <input
        accept={accept}
        aria-label="Upload files"
        className="hidden"
        multiple={multiple}
        onChange={handleChange}
        ref={inputRef}
        title="Upload files"
        type="file"
      />
      <form
        className={cn("w-full", className)}
        onSubmit={handleSubmit}
        ref={formRef}
        {...props}
      >
        <InputGroup
          className={cn("ai-focus-border", inputGroupClassName)}
          style={inputGroupStyle}
        >
          {children}
        </InputGroup>
      </form>
    </>
  );

  return usingProvider ? (
    inner
  ) : (
    <LocalAttachmentsContext.Provider value={ctx}>
      {inner}
    </LocalAttachmentsContext.Provider>
  );
};
